import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Users, Printer, Package, Sparkles, FileText, Layers,
  Mail, Phone, MessageCircle, MapPin, LifeBuoy, Send, CheckCircle2,
} from "lucide-react";

const CONTACT_EMAIL = "contact@trinitylayers.in";
const SUPPORT_EMAIL = "support@trinitylayers.in";
const CONTACT_PHONE = "+917071441999";
const CONTACT_PHONE_DISPLAY = "+91 70714 41999";
const CONTACT_ADDRESS_LINES = ["76/A Nijamuddinpura,", "MAU, Uttar Pradesh,", "275101, India"];
const WHATSAPP_URL = `https://wa.me/${CONTACT_PHONE.replace(/[^0-9]/g, "")}`;

const SUBJECTS = [
  "General Enquiry", "Order Support", "3D Printing",
  "Expert / Designer", "3D Printer Owner", "Shop / Product",
  "Business / Collaboration", "Other",
];

const DISCOVERY = [
  "3D Designers", "3D Printer Owners", "Custom 3D Printing",
  "E-Files / Digital 3D Files", "3D Printed Products", "3D Printers",
  "Filaments", "Resins", "Equipment and related solutions",
];

function Section({ eyebrow, title, children }) {
  return (
    <section className="mb-14">
      {eyebrow && <div className="tl-label mb-2">{eyebrow}</div>}
      {title && <h2 className="text-2xl sm:text-3xl font-display font-semibold mb-4">{title}</h2>}
      <div className="text-[#475569] leading-relaxed max-w-3xl space-y-4">{children}</div>
    </section>
  );
}

export function About() {
  useEffect(() => {
    const prev = document.title;
    document.title = "About — Trinity Layers";
    const meta = document.head.querySelector('meta[name="description"]');
    const prevMeta = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "Trinity Layers is a premium 3D creation, design, manufacturing discovery and commerce platform connecting customers with verified experts, 3D printer owners and quality products.");
    return () => {
      document.title = prev;
      if (meta && prevMeta != null) meta.setAttribute("content", prevMeta);
    };
  }, []);

  return (
    <div data-testid="about-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="mb-12">
        <div className="tl-label mb-3">About</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-5">About Trinity Layers</h1>
        <p className="text-[#475569] text-base sm:text-lg leading-relaxed max-w-3xl">
          Trinity Layers is a premium 3D creation, design, manufacturing discovery and commerce platform. We connect customers with verified experts, 3D printer owners and quality products — in one place.
        </p>
      </div>

      <Section eyebrow="About Trinity Layers">
        <p>Trinity Layers brings the 3D creation ecosystem together in one platform — helping customers discover designers, 3D printer owners, products, digital files, equipment and manufacturing solutions.</p>
      </Section>

      <Section eyebrow="Our Purpose" title="Make 3D creation more accessible">
        <p>Make 3D creation, design and manufacturing more accessible by connecting people with the right expertise, technology and products.</p>
      </Section>

      <Section eyebrow="How it works" title="Discover, request, build">
        <p>Customers can discover verified experts and 3D printer owners, explore products and digital files, request custom work and purchase solutions through the platform.</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          {[
            { icon: Users, title: "Discover experts", body: "Verified 3D designers, engineers and consultants." },
            { icon: Printer, title: "Find a 3D Printer Owner", body: "By technology, materials and location." },
            { icon: Package, title: "Shop ready products", body: "E-Files, printed products, printers, filaments and equipment." },
          ].map((c, i) => (
            <div key={i} className="tl-card p-5">
              <c.icon className="w-5 h-5 text-[#0891B2] mb-3" />
              <div className="font-semibold mb-1 text-slate-900">{c.title}</div>
              <div className="text-sm text-[#475569]">{c.body}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="What you can discover">
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 list-none pl-0">
          {DISCOVERY.map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#0891B2] shrink-0" /><span className="text-[#334155]">{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      <div className="tl-card p-6 mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="font-display font-semibold text-lg">Have a question or want to collaborate?</div>
          <div className="text-sm text-[#475569] mt-1">Our team usually replies within one business day.</div>
        </div>
        <Link to="/contact" data-testid="about-contact-cta" className="tl-btn-primary">Contact our team <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}

function ContactChannel({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <>
      <div className="w-10 h-10 rounded-full bg-[#0891B2]/15 border border-[#0891B2]/30 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#0891B2]" />
      </div>
      <div className="min-w-0">
        <div className="tl-label text-xs mb-0.5">{label}</div>
        <div className="text-sm text-slate-900 truncate">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testId} className="flex items-start gap-3 hover:bg-slate-50 p-3 rounded-lg transition">
      {inner}
    </a>
  ) : (
    <div data-testid={testId} className="flex items-start gap-3 p-3">{inner}</div>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const prev = document.title;
    document.title = "Contact — Trinity Layers";
    return () => { document.title = prev; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (busy) return;
    if (!form.name.trim() || !form.email.trim() || !form.subject || form.message.trim().length < 5) {
      setErr("Please fill in all required fields (message must be at least 5 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(), email: form.email.trim(),
        phone: form.phone.trim() || null, subject: form.subject,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Message sent — we'll reply soon.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="mb-10">
        <div className="tl-label mb-3">Contact</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">Contact Trinity Layers</h1>
        <p className="text-[#475569] max-w-3xl">Have a question, need help with an order, want to work with an expert or 3D printer owner, or want to explore a business opportunity? Get in touch with our team.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          {sent ? (
            <div data-testid="contact-sent" className="tl-card p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-[#0891B2]/15 border border-[#0891B2]/30 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7 text-[#0891B2]" /></div>
              <h2 className="text-2xl font-display font-semibold mb-2">Thanks, {form.name.split(" ")[0] || "there"}!</h2>
              <p className="text-[#475569] mb-6">We've received your message and will reply to <span className="text-slate-900">{form.email}</span> as soon as we can.</p>
              <button data-testid="contact-send-another" onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" }); }} className="tl-btn-secondary">Send another message</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="tl-card p-6 flex flex-col gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1.5">
                  <span className="tl-label text-xs">Full Name<span className="text-[#0891B2]">*</span></span>
                  <input data-testid="contact-name" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tl-input" placeholder="Your full name" />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="tl-label text-xs">Email<span className="text-[#0891B2]">*</span></span>
                  <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="tl-input" placeholder="you@example.com" />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1.5">
                  <span className="tl-label text-xs">Phone Number</span>
                  <input data-testid="contact-phone" value={form.phone} maxLength={30} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="tl-input" placeholder="+91 ..." />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="tl-label text-xs">Subject / Enquiry Type<span className="text-[#0891B2]">*</span></span>
                  <select data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="tl-input">
                    {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1.5">
                <span className="tl-label text-xs">Message<span className="text-[#0891B2]">*</span></span>
                <textarea data-testid="contact-message" required rows={6} maxLength={4000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="tl-input resize-none" placeholder="Tell us how we can help…" />
                <span className="text-xs text-[#64748B]">{form.message.length}/4000</span>
              </label>
              {err && <div data-testid="contact-error" className="tl-alert">{err}</div>}
              <button type="submit" disabled={busy} data-testid="contact-submit" className="tl-btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                {busy ? "Sending…" : (<>Send Message <Send className="w-4 h-4" /></>)}
              </button>
            </form>
          )}
        </div>

        <aside className="lg:col-span-2 flex flex-col gap-4">
          <div className="tl-card p-4">
            <div className="font-display font-semibold mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-[#0891B2]" /> Reach us directly</div>
            <div className="flex flex-col gap-1">
              <ContactChannel icon={Mail} label="General" value={CONTACT_EMAIL} href={`mailto:${CONTACT_EMAIL}`} testId="contact-general-email" />
              <ContactChannel icon={LifeBuoy} label="Support" value={SUPPORT_EMAIL} href={`mailto:${SUPPORT_EMAIL}`} testId="contact-support-email" />
              <ContactChannel icon={Phone} label="Phone" value={CONTACT_PHONE_DISPLAY} href={`tel:${CONTACT_PHONE}`} testId="contact-phone-link" />
              <ContactChannel icon={MessageCircle} label="WhatsApp" value={CONTACT_PHONE_DISPLAY} href={WHATSAPP_URL} testId="contact-whatsapp-link" />
            </div>
          </div>
          <div className="tl-card p-4">
            <div className="font-display font-semibold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#0891B2]" /> Business Address</div>
            <address className="not-italic text-sm text-[#334155] leading-relaxed" data-testid="contact-address">
              {CONTACT_ADDRESS_LINES.map((l) => <div key={l}>{l}</div>)}
            </address>
          </div>
        </aside>
      </div>
    </div>
  );
}
